import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Copy, ShieldCheck, ShieldAlert } from 'lucide-react';
import { confirmPasswordSchema, twoFactorCodeSchema } from '@blimo/shared';
import { Form, FormRootError, InputField, applyProblemToForm } from '../components/form.jsx';
import { Button } from '../components/button.jsx';
import { Badge } from '../components/badge.jsx';
import { Alert, AlertDescription } from '../components/alert.jsx';
import { ConfirmDialog } from '../components/confirm-dialog.jsx';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/dialog.jsx';
import { toast } from '../components/sonner.jsx';

/**
 * @typedef {{
 *   enable: () => Promise<unknown>,
 *   qrCode: () => Promise<{ svg: string, url: string }>,
 *   confirm: (payload: { code: string }) => Promise<unknown>,
 *   recoveryCodes: () => Promise<string[]>,
 *   regenerateRecoveryCodes: () => Promise<unknown>,
 *   disable: () => Promise<unknown>,
 *   confirmPassword: (payload: { password: string }) => Promise<unknown>,
 * }} TwoFactorActions
 */

/**
 * Wraps actions that Fortify protects with "password.confirm" (HTTP 423):
 * on 423 a password dialog opens, then the action is retried once.
 * @param {(payload: { password: string }) => Promise<unknown>} confirmPassword
 */
function usePasswordConfirmation(confirmPassword) {
  const [pending, setPending] = useState(/** @type {null | { resolve: (v: boolean) => void }} */ (null));

  /** @template T @param {() => Promise<T>} action @returns {Promise<T>} */
  async function run(action) {
    try {
      return await action();
    } catch (error) {
      if (/** @type {any} */ (error)?.status !== 423) throw error;
      const confirmed = await new Promise((resolve) => setPending({ resolve }));
      if (!confirmed) throw Object.assign(new Error('Abgebrochen'), { cancelled: true });
      return action();
    }
  }

  const dialog = (
    <PasswordConfirmDialog
      open={Boolean(pending)}
      onCancel={() => {
        pending?.resolve(false);
        setPending(null);
      }}
      onConfirm={async (values) => {
        await confirmPassword(values);
        pending?.resolve(true);
        setPending(null);
      }}
    />
  );
  return { run, dialog };
}

function PasswordConfirmDialog({ open, onCancel, onConfirm }) {
  const form = useForm({ resolver: zodResolver(confirmPasswordSchema), defaultValues: { password: '' } });
  async function submit(values) {
    try {
      await onConfirm(values);
      form.reset();
    } catch (error) {
      applyProblemToForm(error, form.setError, { fields: ['password'] });
    }
  }
  return (
    <Dialog open={open} onOpenChange={(value) => !value && onCancel()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Passwort bestätigen</DialogTitle>
          <DialogDescription>Bitte bestätigen Sie Ihr Passwort, um fortzufahren.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-4" noValidate>
            <FormRootError />
            <InputField name="password" label="Passwort" type="password" autoComplete="current-password" autoFocus />
            <Button type="submit" loading={form.formState.isSubmitting}>
              Bestätigen
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

/** Extracts the TOTP secret from an otpauth:// URL for manual entry. */
function secretFromUrl(url) {
  try {
    return new URL(url).searchParams.get('secret');
  } catch {
    return null;
  }
}

/** @param {{ codes: string[] }} props */
export function RecoveryCodesList({ codes }) {
  async function copy() {
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      toast.success('Wiederherstellungscodes kopiert');
    } catch {
      toast.error('Kopieren nicht möglich');
    }
  }
  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm text-muted-foreground">
        Bewahren Sie diese Codes sicher auf (z. B. im Passwortmanager). Jeder Code kann einmal verwendet werden, falls Sie keinen Zugriff auf Ihre
        Authenticator-App haben.
      </p>
      <ul className="grid grid-cols-2 gap-1 rounded-md border bg-muted p-3 font-mono text-sm">
        {codes.map((code) => (
          <li key={code}>{code}</li>
        ))}
      </ul>
      <Button variant="outline" size="sm" className="self-start" onClick={copy}>
        <Copy /> Kopieren
      </Button>
    </div>
  );
}

/**
 * Two-factor management (enable → scan QR → confirm code → recovery codes; show/regenerate codes; disable).
 * @param {{
 *   enabled: boolean,
 *   required?: boolean,
 *   actions: TwoFactorActions,
 *   onChanged?: () => void | Promise<unknown>,
 * }} props
 */
export function TwoFactorSettings({ enabled, required = false, actions, onChanged }) {
  const [step, setStep] = useState(/** @type {'idle'|'setup'|'done'} */ ('idle'));
  const [qr, setQr] = useState(/** @type {null | { svg: string, url: string }} */ (null));
  const [codes, setCodes] = useState(/** @type {string[] | null} */ (null));
  const [busy, setBusy] = useState(/** @type {string|null} */ (null));
  const { run, dialog } = usePasswordConfirmation(actions.confirmPassword);

  async function guarded(name, fn) {
    setBusy(name);
    try {
      await fn();
    } catch (error) {
      if (!(/** @type {any} */ (error)?.cancelled)) toast.error(error?.detail || error?.title || error?.message || 'Aktion fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  }

  const startSetup = () =>
    guarded('enable', async () => {
      await run(() => actions.enable());
      setQr(await actions.qrCode());
      setStep('setup');
    });

  const showCodes = () =>
    guarded('codes', async () => {
      setCodes(await run(() => actions.recoveryCodes()));
    });

  const regenerate = () =>
    guarded('regenerate', async () => {
      await run(() => actions.regenerateRecoveryCodes());
      setCodes(await actions.recoveryCodes());
      toast.success('Neue Wiederherstellungscodes erzeugt');
    });

  const disable = async () => {
    await guarded('disable', async () => {
      await run(() => actions.disable());
      setCodes(null);
      setStep('idle');
      toast.success('Zwei-Faktor-Authentifizierung deaktiviert');
      await onChanged?.();
    });
  };

  return (
    <div className="flex flex-col gap-4">
      {dialog}
      <div className="flex items-center gap-2">
        {enabled ? (
          <Badge variant="success">
            <ShieldCheck aria-hidden="true" /> Aktiv
          </Badge>
        ) : (
          <Badge variant={required ? 'warning' : 'neutral'}>
            <ShieldAlert aria-hidden="true" /> Nicht eingerichtet
          </Badge>
        )}
        {required && <span className="text-sm text-muted-foreground">Für Ihre Rolle verpflichtend.</span>}
      </div>

      {!enabled && step === 'idle' && (
        <div className="flex flex-col gap-3">
          <p className="text-sm text-muted-foreground">
            Mit der Zwei-Faktor-Authentifizierung benötigen Sie bei der Anmeldung zusätzlich einen Code aus einer Authenticator-App (z. B. Microsoft
            Authenticator, Google Authenticator, 1Password).
          </p>
          <Button className="self-start" onClick={startSetup} loading={busy === 'enable'}>
            Zwei-Faktor einrichten
          </Button>
        </div>
      )}

      {step === 'setup' && qr && (
        <SetupStep
          qr={qr}
          onConfirm={async (code) => {
            await run(() => actions.confirm({ code }));
            setCodes(await actions.recoveryCodes());
            setStep('done');
            toast.success('Zwei-Faktor-Authentifizierung aktiviert');
            await onChanged?.();
          }}
        />
      )}

      {codes && <RecoveryCodesList codes={codes} />}

      {enabled && step !== 'setup' && (
        <div className="flex flex-wrap gap-2">
          {!codes && (
            <Button variant="outline" onClick={showCodes} loading={busy === 'codes'}>
              Wiederherstellungscodes anzeigen
            </Button>
          )}
          <Button variant="outline" onClick={regenerate} loading={busy === 'regenerate'}>
            Neue Codes erzeugen
          </Button>
          {!required && (
            <ConfirmDialog
              destructive
              title="Zwei-Faktor-Authentifizierung deaktivieren?"
              description="Ihr Konto ist danach nur noch durch das Passwort geschützt."
              confirmLabel="Deaktivieren"
              onConfirm={disable}
              trigger={
                <Button variant="ghost" className="text-destructive" loading={busy === 'disable'}>
                  Deaktivieren
                </Button>
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

function SetupStep({ qr, onConfirm }) {
  const form = useForm({ resolver: zodResolver(twoFactorCodeSchema), defaultValues: { code: '' } });
  const secret = secretFromUrl(qr.url);
  async function submit(values) {
    try {
      await onConfirm(values.code);
    } catch (error) {
      if (error?.cancelled) return;
      applyProblemToForm(error, form.setError, { fields: ['code'] });
    }
  }
  return (
    <div className="flex flex-col gap-4">
      <ol className="list-decimal space-y-1 pl-5 text-sm">
        <li>Scannen Sie den QR-Code mit Ihrer Authenticator-App.</li>
        <li>Geben Sie den angezeigten 6-stelligen Code ein.</li>
      </ol>
      {/* SVG is generated by our own backend (Fortify / bacon-qr-code). */}
      <div
        className="w-fit rounded-md border bg-white p-3 [&_svg]:size-44"
        role="img"
        aria-label="QR-Code für die Authenticator-App"
        dangerouslySetInnerHTML={{ __html: qr.svg }}
      />
      {secret && (
        <details className="text-sm">
          <summary className="cursor-pointer text-muted-foreground">QR-Code kann nicht gescannt werden?</summary>
          <p className="mt-2">
            Schlüssel manuell eingeben: <code className="break-all rounded bg-muted px-1 py-0.5 font-mono">{secret}</code>
          </p>
        </details>
      )}
      <Form {...form}>
        <form onSubmit={form.handleSubmit(submit)} className="flex max-w-xs flex-col gap-3" noValidate>
          <FormRootError />
          <InputField name="code" label="Bestätigungscode" inputMode="numeric" autoComplete="one-time-code" maxLength={6} />
          <Button type="submit" loading={form.formState.isSubmitting} className="self-start">
            Aktivieren
          </Button>
        </form>
      </Form>
      <Alert variant="info">
        <AlertDescription>Nach der Aktivierung werden Ihnen Wiederherstellungscodes angezeigt.</AlertDescription>
      </Alert>
    </div>
  );
}
